import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiArrowLeft, FiDownload, FiTrash2 } from "react-icons/fi";
import { Modal } from "../../Components/Modal";
import { usePermissions } from "../../Auth/PermissionsContext";
import {
	shopClient,
	eurCents,
	fmtDate,
	fmtDateTime,
	type OrderStatus,
	type ShopCustomerDetail,
	type ShopOrderListItem,
} from "./shopClient";

const STATUS_LABEL: Record<OrderStatus, string> = {
	Pending: "En attente",
	Paid: "Payée",
	Fulfilled: "Expédiée",
	Refunded: "Remboursée",
	Cancelled: "Annulée",
};

const STATUS_TONE: Record<OrderStatus, string> = {
	Paid: "bg-emerald-50 text-emerald-700",
	Fulfilled: "bg-emerald-50 text-emerald-700",
	Pending: "bg-amber-50 text-amber-700",
	Refunded: "bg-slate-100 text-slate-600",
	Cancelled: "bg-rose-50 text-rose-600",
};

/** Order status pill with the admin slate tone mapping. */
function StatusBadge({ status }: { status: OrderStatus }): React.ReactElement {
	return (
		<span className={"rounded-full px-2.5 py-0.5 text-xs font-medium " + (STATUS_TONE[status] ?? "bg-slate-100 text-slate-600")}>
			{STATUS_LABEL[status] ?? status}
		</span>
	);
}

/** Customer profile, orders, addresses, GDPR export & deletion. */
export default function CustomerDetail(): React.ReactElement {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();
	const { can } = usePermissions();
	const canWrite = can("feature:customers:write");
	const canDelete = canWrite;

	const [customer, setCustomer] = useState<ShopCustomerDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [exporting, setExporting] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);

	// Pro / B2B
	const [proEnabled, setProEnabled] = useState(false);
	const [company, setCompany] = useState("");
	const [vat, setVat] = useState("");
	const [savingPro, setSavingPro] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await shopClient.customers.get(id);
			setCustomer(res);
			setProEnabled(res.isPro);
			setCompany(res.company ?? "");
			setVat(res.vatNumber ?? "");
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
			setCustomer(null);
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		void load();
	}, [load]);

	const metadataEntries = useMemo(
		() => Object.entries(customer?.metadata ?? {}).filter(([, v]) => v != null && String(v).trim() !== ""),
		[customer]
	);

	const handleExport = useCallback(async () => {
		setExporting(true);
		try {
			const data = await shopClient.customers.export(id);
			const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "client-" + id + ".json";
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
			toast.success("Données exportées");
		} catch (e: any) {
			toast.error(e?.message ?? "Erreur");
		} finally {
			setExporting(false);
		}
	}, [id]);

	const handleSavePro = useCallback(async () => {
		setSavingPro(true);
		try {
			const updated = await shopClient.customers.setPro(id, proEnabled, company.trim() || undefined, vat.trim() || undefined);
			setCustomer(updated);
			toast.success(proEnabled ? "Compte Pro activé" : "Compte Pro désactivé");
		} catch (e: any) {
			toast.error(e?.message ?? "Erreur");
		} finally {
			setSavingPro(false);
		}
	}, [id, proEnabled, company, vat]);

	const handleDelete = useCallback(async () => {
		setDeleting(true);
		try {
			const res = await shopClient.customers.remove(id);
			toast.success("Compte supprimé — " + res.anonymisedOrders + " commande(s) anonymisée(s)");
			setConfirmOpen(false);
			history.push("/customers");
		} catch (e: any) {
			toast.error(e?.message ?? "Erreur");
		} finally {
			setDeleting(false);
		}
	}, [id, history]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				<Link
					to="/customers"
					className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700"
				>
					<FiArrowLeft />
					Retour aux clients
				</Link>

				{loading ? (
					<div className="rounded-2xl border border-slate-100 bg-white p-10 text-center text-slate-400 shadow-sm">Chargement…</div>
				) : error ? (
					<div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
				) : !customer ? (
					<div className="rounded-2xl border border-slate-100 bg-white p-10 text-center text-slate-400 shadow-sm">Client introuvable</div>
				) : (
					<>
						<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
							<div className="min-w-0">
								<p className="text-xs uppercase tracking-widest text-slate-500">Boutique · Client</p>
								<h1 className="truncate text-2xl font-semibold text-slate-900">{customer.name || customer.email}</h1>
								<div className="mt-2 flex flex-wrap items-center gap-1.5">
									{customer.confirmed ? (
										<span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Confirmé</span>
									) : (
										<span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">Non confirmé</span>
									)}
									{customer.banned && (
										<span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-600">Banni</span>
									)}
									{customer.isPro && (
										<span className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-semibold text-white">PRO</span>
									)}
								</div>
							</div>
						</div>

						<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
							<div className="space-y-6 lg:col-span-2">
								<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
									<div className="border-b border-slate-100 px-5 py-3">
										<h2 className="text-sm font-semibold text-slate-700">Commandes</h2>
									</div>
									{customer.orders.length === 0 ? (
										<div className="px-5 py-10 text-center text-slate-400">Aucune commande</div>
									) : (
										<div className="overflow-x-auto">
											<table className="min-w-full divide-y divide-slate-100 text-sm">
												<thead className="bg-slate-50">
													<tr>
														<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">#</th>
														<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Date</th>
														<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Statut</th>
														<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Total</th>
													</tr>
												</thead>
												<tbody className="divide-y divide-slate-100">
													{customer.orders.map((o: ShopOrderListItem) => (
														<tr key={o.id} className="hover:bg-slate-50">
															<td className="px-5 py-3">
																<Link to={"/orders/" + o.id} className="font-medium text-slate-900 hover:underline">
																	{o.id}
																</Link>
															</td>
															<td className="px-5 py-3 text-slate-600">{fmtDate(o.createdAt)}</td>
															<td className="px-5 py-3"><StatusBadge status={o.status} /></td>
															<td className="px-5 py-3 font-medium text-slate-900">{eurCents(o.totalCents, o.currency)}</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}
								</div>

								<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
									<div className="border-b border-slate-100 px-5 py-3">
										<h2 className="text-sm font-semibold text-slate-700">Adresses</h2>
									</div>
									{customer.addresses.length === 0 ? (
										<div className="px-5 py-6 text-sm text-slate-400">Aucune adresse</div>
									) : (
										<div className="divide-y divide-slate-100">
											{customer.addresses.map((a, i) => (
												<div key={i} className="px-5 py-4 text-sm text-slate-600">
													{a.name && <p className="font-medium text-slate-900">{a.name}</p>}
													{a.line1 && <p>{a.line1}</p>}
													{a.line2 && <p>{a.line2}</p>}
													{(a.postalCode || a.city) && (
														<p>{[a.postalCode, a.city].filter(Boolean).join(" ")}</p>
													)}
													{a.country && <p>{a.country}</p>}
												</div>
											))}
										</div>
									)}
								</div>
							</div>

							<div className="space-y-4 lg:col-span-1">
								<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
									<div className="border-b border-slate-100 px-5 py-3">
										<h2 className="text-sm font-semibold text-slate-700">Profil</h2>
									</div>
									<dl className="space-y-3 px-5 py-4 text-sm">
										<div className="flex justify-between gap-3">
											<dt className="text-slate-500">E-mail</dt>
											<dd className="text-right font-medium text-slate-900 break-all">{customer.email}</dd>
										</div>
										<div className="flex justify-between gap-3">
											<dt className="text-slate-500">Nom</dt>
											<dd className="text-right text-slate-700">{customer.name || "—"}</dd>
										</div>
										<div className="flex justify-between gap-3">
											<dt className="text-slate-500">Téléphone</dt>
											<dd className="text-right text-slate-700">{customer.phone || "—"}</dd>
										</div>
										<div className="flex justify-between gap-3">
											<dt className="text-slate-500">Inscrit</dt>
											<dd className="text-right text-slate-700">{fmtDateTime(customer.createdAt)}</dd>
										</div>
										<div className="flex justify-between gap-3">
											<dt className="text-slate-500">Dernière connexion</dt>
											<dd className="text-right text-slate-700">{fmtDateTime(customer.lastSignInAt)}</dd>
										</div>
										{metadataEntries.length > 0 && (
											<div className="space-y-2 border-t border-slate-100 pt-3">
												{metadataEntries.map(([k, v]) => (
													<div key={k} className="flex justify-between gap-3 text-xs">
														<dt className="text-slate-400">{k}</dt>
														<dd className="text-right text-slate-500 break-all">{String(v)}</dd>
													</div>
												))}
											</div>
										)}
									</dl>
								</div>

								<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
									<div className="border-b border-slate-100 px-5 py-3">
										<h2 className="text-sm font-semibold text-slate-700">Statistiques</h2>
									</div>
									<dl className="space-y-3 px-5 py-4 text-sm">
										<div className="flex justify-between gap-3">
											<dt className="text-slate-500">Commandes</dt>
											<dd className="font-semibold text-slate-900">{customer.ordersCount}</dd>
										</div>
										<div className="flex justify-between gap-3">
											<dt className="text-slate-500">Total dépensé</dt>
											<dd className="font-semibold text-slate-900">{eurCents(customer.totalSpentCents)}</dd>
										</div>
									</dl>
								</div>

								<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
									<div className="border-b border-slate-100 px-5 py-3">
										<h2 className="text-sm font-semibold text-slate-700">Compte Pro (B2B)</h2>
									</div>
									<div className="space-y-3 px-5 py-4">
										<label className="flex items-center gap-2 text-sm font-medium text-slate-700">
											<input type="checkbox" checked={proEnabled} disabled={!canWrite}
												onChange={(e) => setProEnabled(e.target.checked)}
												className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
											Client professionnel
										</label>
										<div>
											<label className="mb-1 block text-xs font-medium text-slate-500">Entreprise</label>
											<input value={company} disabled={!canWrite} onChange={(e) => setCompany(e.target.value)}
												className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" />
										</div>
										<div>
											<label className="mb-1 block text-xs font-medium text-slate-500">N° TVA</label>
											<input value={vat} disabled={!canWrite} onChange={(e) => setVat(e.target.value)}
												placeholder="BE0123456789"
												className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none" />
										</div>
										{canWrite && (
											<button type="button" onClick={handleSavePro} disabled={savingPro}
												className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
												{savingPro ? "Enregistrement…" : "Enregistrer le statut Pro"}
											</button>
										)}
									</div>
								</div>

								<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
									<div className="border-b border-slate-100 px-5 py-3">
										<h2 className="text-sm font-semibold text-slate-700">RGPD</h2>
									</div>
									<div className="space-y-3 px-5 py-4">
										<p className="text-xs text-slate-500">
											Exportez l'ensemble des données du client (profil, commandes, adresses) ou supprimez
											définitivement le compte conformément au droit à l'effacement.
										</p>
										<button
											type="button"
											onClick={handleExport}
											disabled={exporting}
											className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
										>
											<FiDownload />
											Exporter les données
										</button>
										{canDelete && (
											<button
												type="button"
												onClick={() => setConfirmOpen(true)}
												className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
											>
												<FiTrash2 />
												Supprimer le compte (RGPD)
											</button>
										)}
									</div>
								</div>
							</div>
						</div>

						<Modal show={confirmOpen} onHide={() => setConfirmOpen(false)} size="md" centered>
							<Modal.Header closeButton>
								<Modal.Title>Supprimer le compte</Modal.Title>
							</Modal.Header>
							<Modal.Body className="space-y-4">
								<p className="text-sm text-slate-600">
									Cette action est <span className="font-semibold text-rose-600">irréversible</span>. Elle supprime
									le compte Supabase de <span className="font-medium text-slate-900">{customer.email}</span> et
									anonymise ses commandes (les données personnelles sont effacées, les enregistrements comptables
									sont conservés).
								</p>
								<p className="text-sm text-slate-500">Confirmez-vous la suppression conformément au RGPD ?</p>
							</Modal.Body>
							<Modal.Footer>
								<button
									type="button"
									onClick={() => setConfirmOpen(false)}
									disabled={deleting}
									className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
								>
									Annuler
								</button>
								<button
									type="button"
									onClick={handleDelete}
									disabled={deleting}
									className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
								>
									<FiTrash2 />
									{deleting ? "Suppression…" : "Supprimer définitivement"}
								</button>
							</Modal.Footer>
						</Modal>
					</>
				)}
			</div>
		</div>
	);
}
